import { FlowForm, useFlowFormGlobal, FlowFormLeaveContext, HelpPopup } from 't-lib-ui/structure';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import MainLayout from '../components/MainLayout';
import PageHeader from '../components/PageHeader';
import HeaderTools from '../components/HeaderTools';
import { Checkbox, FocusText, Input, TextButton } from 't-lib-ui/widgets';
import { usePreferences } from '../connector/preferences';
import { EMBED_SERVICES } from '../schema-copy/api-schema';
import { SERVICE_NAMES } from 't-lib-postviewer';
import { getJurisdiction } from '../connector/geo';
import { defaultTheme, supportsAutoTheme } from '../theme';
import { resetBankruptcyDismissal } from '../components/RssBankruptcyCard';
import { AppState } from '../context/general';
import { isSuperuser } from '../schema-copy/capabilities';
import { useOfflineDownloadProgress } from '../utils/useOfflineDownloadProgress';
import { isNativeApp } from '../utils/platform';
import { useContext, useEffect, useState } from 'react';
import './PreferencesPage.css';

const PreferencesPage: React.FC = () => {
  useFlowFormGlobal();

  const appState = useContext(AppState);
  const [preferences, updatePreferences] = usePreferences();
  const [showPostNavHelp, setShowPostNavHelp] = useState(false);

  // Get current values with defaults
  const theme = preferences.theme ?? defaultTheme;
  const postNav = preferences.postNav ?? 'autohide';
  const longPressDuration = preferences.longPressDuration ?? 1000;
  const [longPressDurationDraft, setLongPressDurationDraft] = useState(String(longPressDuration));
  const showAllFeeds = preferences.showAllFeeds ?? 'unread';
  const homeGrouping = preferences.homeGrouping ?? 'feeds';
  // Offline reading is meant for the native apps (logged-in users only: the anonymous engine has no
  // offline snapshot); on the web only admins see it, for testing.
  const showOffline = isNativeApp ? appState.state.loginState == 'ok' : isSuperuser(appState.state.authData?.selectedMembership?.capabilities);
  const offlineReading = preferences.offlineReading ?? false;
  const offlineProgress = useOfflineDownloadProgress();
  // Storage the browser reports for this site (the offline cache is nearly all of it). Refreshed
  // when a download run ends and after clearing.
  const [storageUsed, setStorageUsed] = useState<number>();
  const [clearingCache, setClearingCache] = useState(false);
  const refreshStorageUsed = () =>
    navigator.storage?.estimate?.().then(e => setStorageUsed(e.usage)).catch(() => undefined);
  useEffect(() => { if (showOffline && !offlineProgress.active) refreshStorageUsed(); }, [showOffline, offlineProgress.active]);
  const clearOfflineCache = async () => {
    setClearingCache(true);
    try { await appState.feedEngine.clearOfflineCache(); }
    finally {
      setClearingCache(false);
      refreshStorageUsed();
    }
  };

  // RSS Bankruptcy helper state.
  const bkState = preferences.bankruptcyState ?? 'none';
  const bkDelayActive = bkState == 'delay' && !!preferences.bankruptcyDelayUntil && Date.now() < Date.parse(preferences.bankruptcyDelayUntil);
  // Manual re-enable should re-ask right away (sticky 'question'), not wait for inactivity.
  // If the backlog is already under 500, the card's effect will quietly drop it back to 'none'.
  const reenableBankruptcy = () => { updatePreferences({ bankruptcyState: 'question' }); resetBankruptcyDismissal(); };
  const snoozeBankruptcy = () => updatePreferences({ bankruptcyState: 'delay', bankruptcyDelayUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() });
  const disableBankruptcy = () => updatePreferences({ bankruptcyState: 'never' });

  // Explicit per-service entries (not a cleared map) so the choice survives a jurisdiction change.
  const setAllEmbedConsent = (allowed: boolean) =>
    updatePreferences({ embedConsent: Object.fromEntries(EMBED_SERVICES.map(svc => [svc, allowed])) });

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
    <HelpPopup
      show={showPostNavHelp}
      title="Post navigation"
      onClose={() => setShowPostNavHelp(false)}
      closable={true}
    >
      <p style={{ marginTop: 0 }}>
        Two navigation controls help you move between posts:
      </p>
      <p>
        <strong>Floating buttons.</strong> Up/down buttons appear in the bottom-right corner of
        the post view. They're always available, regardless of screen size.
      </p>
      <p>
        <strong>Gutter chevrons.</strong> When the post view is wide enough (roughly 1500px or more),
        faint left/right chevrons appear in the empty space on either side of the post column.
        Clicking anywhere in that area jumps to the previous or next post. A chevron disappears
        when there is nothing to navigate to on that side (e.g. the left chevron hides when you
        are at the top of the first post).
      </p>
      <p>
        <strong>Autohide</strong> keeps the gutter chevrons visible whenever they're available,
        but hides the floating buttons while you scroll down and brings them back as soon as you
        scroll up or use the keyboard. This keeps the reading view clean while still giving you
        quick access to navigation.
      </p>
      <p>
        <strong>Always</strong> keeps both sets of controls on screen at all times. <strong>Never</strong> disables
        both of them — use keyboard shortcuts (j/k or arrow keys) to move between posts instead.
      </p>
    </HelpPopup>
    <MainLayout
      selectedFeed="preferences"
      right={({ onExpand }) => (
        <div className="preferencesPage">
          {/* Header */}
          <PageHeader title="Preferences" onMenu={onExpand} rightContent={<HeaderTools />} />

          {/* Posts Preferences */}
          <FlowForm main className="preferencesSection">
            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">Appearance</h2>
              <div className="preferencesGrid">
                {/* Theme */}
                <div className="preferenceCard">
                  <div className="preferenceCardHeader">
                    <h3>Dark theme</h3>
                    <p>
                      Use a dark color scheme. <strong>Auto</strong> follows your
                      device's system setting.
                    </p>
                  </div>
                  <div className="preferenceCardControl">
                    <div className="radioGroup">
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={theme == 'light'}
                          onToggle={() => updatePreferences({ theme: 'light' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>Off</span>
                      </label>
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={theme == 'dark'}
                          onToggle={() => updatePreferences({ theme: 'dark' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>On</span>
                      </label>
                      {supportsAutoTheme && (
                        <label className="radioOption">
                          <Checkbox
                            type="radio"
                            checked={theme == 'auto'}
                            onToggle={() => updatePreferences({ theme: 'auto' })}
                            focussable="flow"
                            size="small"
                          />
                          <span>Auto</span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">Posts</h2>
              <div className="preferencesGrid">
                {/* Offline reading */}
                {showOffline && (
                  <div className="preferenceCard">
                    <div className="preferenceCardHeader">
                      <h3>Offline reading</h3>
                      <p>
                        Keep your feeds and posts stored on this device, so you can keep reading
                        without a connection.{!isNativeApp && ' Intended for the apps; available here for testing.'}{' '}
                        Downloaded posts and images stay when you switch it off, so switching it on
                        again is quick. <strong>Clear cache</strong> deletes them.
                      </p>
                    </div>
                    <div className="preferenceCardControl">
                      <div className="radioGroup">
                        <label className="radioOption">
                          <Checkbox
                            type="radio"
                            checked={!offlineReading}
                            onToggle={() => updatePreferences({ offlineReading: false })}
                            focussable="flow"
                            size="small"
                          />
                          <span>Off</span>
                        </label>
                        <label className="radioOption">
                          <Checkbox
                            type="radio"
                            checked={offlineReading}
                            onToggle={() => updatePreferences({ offlineReading: true })}
                            focussable="flow"
                            size="small"
                          />
                          <span>On</span>
                        </label>
                      </div>
                      {offlineReading && (
                        <div className="offlineDownloadStatus" role="status">
                          {offlineProgress.active ? (
                            <>
                              <progress className="offlineDownloadBar" max={offlineProgress.total + offlineProgress.imagesTotal || 1}
                                value={offlineProgress.done + offlineProgress.imagesDone + offlineProgress.imagesFailed} />
                              <span>
                                Downloading posts: {offlineProgress.done} of {offlineProgress.total} files
                                {!!offlineProgress.imagesTotal && <>, images: {offlineProgress.imagesDone} of {offlineProgress.imagesTotal}</>}
                              </span>
                            </>
                          ) : (
                            <>
                              <span>
                                {offlineProgress.failed
                                  ? `${offlineProgress.failed} of ${offlineProgress.total} files could not be downloaded. They are retried automatically.`
                                  : 'Your unread, starred and read-later posts and their images are stored on this device.'}
                              </span>
                              {!!offlineProgress.imagesFailed && (
                                <span>{offlineProgress.imagesFailed} of {offlineProgress.imagesTotal} images could not be stored.</span>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <div className="offlineCacheRow">
                        <span className="offlineDownloadStatus">
                          {storageUsed != undefined && `Storage used on this device: ${formatBytes(storageUsed)}`}
                        </span>
                        <TextButton label="Clear cache" type="minor" busy={clearingCache} onClick={clearOfflineCache} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Post Navigation */}
                <div className="preferenceCard">
                  <div className="preferenceCardHeader">
                    <h3>Post navigation</h3>
                    <p>
                      Show navigation controls to jump between posts: floating up/down buttons
                      in the bottom-right corner, and, on wide screens, clickable chevron zones
                      in the empty space to the left and right of the post column.
                      {' '}<FocusText className="feedConfigShowMe" focusVisible={false} focussable="flow" onClick={() => setShowPostNavHelp(true)}>Learn more</FocusText>
                    </p>
                  </div>
                  <div className="preferenceCardControl">
                    <div className="radioGroup">
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={postNav == 'autohide'}
                          onToggle={() => updatePreferences({ postNav: 'autohide' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>Autohide</span>
                      </label>
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={postNav == 'always'}
                          onToggle={() => updatePreferences({ postNav: 'always' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>Always</span>
                      </label>
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={postNav == 'never'}
                          onToggle={() => updatePreferences({ postNav: 'never' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>Never</span>
                      </label>
                    </div>
                  </div>
                </div>
                {/* Long-press duration */}
                <div className="preferenceCard">
                  <div className="preferenceCardHeader">
                    <h3>Long-press duration</h3>
                    <p>
                      When you reach the last post in a feed, you can press and hold the down
                      navigation button (or the right gutter) to jump to the next unread feed.
                      This setting controls how long you need to hold before that jump triggers.
                      Lower values feel snappier; higher values help avoid accidental jumps.
                      Allowed range: 400&ndash;5000 milliseconds.
                    </p>
                  </div>
                  <div className="preferenceCardControl">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 120 }}>
                      <Input
                        type="number"
                        value={longPressDurationDraft}
                        min={400}
                        max={5000}
                        onInput={value => {
                          setLongPressDurationDraft(value);
                          const n = Number(value);
                          if (Number.isFinite(n) && n >= 400 && n <= 5000)
                            updatePreferences({ longPressDuration: n });
                        }}
                        onBlur={() => {
                          const n = Number(longPressDurationDraft);
                          const clamped = !Number.isFinite(n) ? 1000 : Math.min(5000, Math.max(400, Math.round(n)));
                          setLongPressDurationDraft(String(clamped));
                          if (clamped != longPressDuration) updatePreferences({ longPressDuration: clamped });
                        }}
                      />
                      <span>ms</span>
                    </div>
                  </div>
                </div>
              </div>


            </div>

          {/* Feeds Preferences */}
            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">Feeds</h2>
              <div className="preferencesGrid">
                {/* Show all feeds */}
                <div className="preferenceCard">
                  <div className="preferenceCardHeader">
                    <h3>Show all feeds</h3>
                    <p>Control whether feeds without unread posts are hidden.</p>
                  </div>
                  <div className="preferenceCardControl">
                    <div className="radioGroup">
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={showAllFeeds == 'yes'}
                          onToggle={() => updatePreferences({ showAllFeeds: 'yes' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>Show all feeds</span>
                      </label>
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={showAllFeeds == 'unread'}
                          onToggle={() => updatePreferences({ showAllFeeds: 'unread' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>Only feeds with unread posts</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Home page unread grouping */}
                <div className="preferenceCard">
                  <div className="preferenceCardHeader">
                    <h3>Group Home page by folders</h3>
                    <p>
                      Show the "New posts available" list on the Home page as individual feeds,
                      or grouped into your folders. Feeds without a folder always show
                      individually, and the folder view only kicks in when a folder has more
                      than one feed with unread posts.
                    </p>
                  </div>
                  <div className="preferenceCardControl">
                    <div className="radioGroup">
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={homeGrouping == 'feeds'}
                          onToggle={() => updatePreferences({ homeGrouping: 'feeds' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>Feeds</span>
                      </label>
                      <label className="radioOption">
                        <Checkbox
                          type="radio"
                          checked={homeGrouping == 'folders'}
                          onToggle={() => updatePreferences({ homeGrouping: 'folders' })}
                          focussable="flow"
                          size="small"
                        />
                        <span>Folders</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* RSS Bankruptcy helper */}
                <div className="preferenceCard">
                  <div className="preferenceCardHeader">
                    <h3>RSS Bankruptcy helper</h3>
                    <p>
                      When a large unread backlog piles up, a card on Home and My Feeds offers quick ways to catch up.
                      {bkState == 'never'
                        ? ' It is currently turned off.'
                        : bkDelayActive
                        ? ` It is snoozed until ${new Date(preferences.bankruptcyDelayUntil!).toLocaleDateString()}.`
                        : ' It appears automatically when needed.'}
                    </p>
                  </div>
                  <div className="preferenceCardControl">
                    <div className="preferenceButtonRow">
                      {(bkState == 'never' || bkDelayActive)
                        ? <TextButton label="Re-enable" type="minor" onClick={reenableBankruptcy} />
                        : <TextButton label="Snooze for 7 days" type="minor" onClick={snoozeBankruptcy} />}
                      {bkState != 'never' && <TextButton label="Turn off forever" type="minor" focussable="sideway" onClick={disableBankruptcy} />}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          {/* External content */}
            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">External content</h2>
              <div className="preferencesGrid">
                <div className="preferenceCard">
                  <div className="preferenceCardHeader">
                    <h3>Embedded content</h3>
                    <p>
                      Posts can embed videos, podcast players and social media posts from the following
                      services. Loading an embed
                      connects your browser to that service. When a service is disabled, embeds
                      show a placeholder instead. Clicking it loads the embed and enables the
                      service again. 
                    </p>
                  </div>
                  <div className="preferenceCardControl">
                    <div className="radioGroup">
                      {EMBED_SERVICES.map(svc => {
                        const allowed = preferences.embedConsent?.[svc] ?? getJurisdiction() != 'EU';
                        return (
                          <label key={svc} className="radioOption">
                            <Checkbox
                              checked={allowed}
                              onToggle={() => updatePreferences({ embedConsent: { ...(preferences.embedConsent ?? {}), [svc]: !allowed } })}
                              focussable="flow"
                              size="small"
                            />
                            <span>{SERVICE_NAMES[svc]}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="preferenceButtonRow">
                      <TextButton label="Allow all" type="minor" onClick={() => setAllEmbedConsent(true)} />
                      <TextButton label="Disable all" type="minor" focussable="sideway" onClick={() => setAllEmbedConsent(false)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {preferences.dismissed && (
              <div className="preferencesSectionContent">
                <h2 className="preferencesSectionTitle">Popups</h2>
                <div className="preferencesGrid">
                  <div className="preferenceCard">
                    <div className="preferenceCardHeader">
                      <h3>Dismissed popups</h3>
                      <p>Re-enable all one-time hint popups that you have previously dismissed.</p>
                    </div>
                    <div className="preferenceCardControl">
                      <div className="preferenceButtonRow">
                        <TextButton label="Re-enable" type="minor" onClick={() => updatePreferences({ dismissed: '' })} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </FlowForm>
        </div>
      )}
      startWithLeftCollapsed={false}
    />
    </FlowFormLeaveContext.Provider>
  );
};

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default PreferencesPage;
